import React, { useState, useEffect, useRef } from "react";
import Search from "./img/Search.svg";
import notification from "./img/notification.svg";
import ArrowRight from "./img/ArrowRight.svg";
import User from "./img/User.svg";
import HomeImg from "./img/HomeImg.svg";
import RightWhite from "./img/RightWhite.svg";
import Rectangle from "./img/Rectangle.svg";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosInstance";
import HomeHeader from "./customer/HomeHeader";

const HomePage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [singleProject, setSingleProject] = useState(null);
  const [projectCode, setProjectCode] = useState("PRJCT0579");
  const [loading, setLoading] = useState(true); // initial loader
  const [loadingMore, setLoadingMore] = useState(false); // loader for next pages
  const [nextUrl, setNextUrl] = useState(null); // API next link
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);

  const listScrollRef = useRef(null);
  const sentinelRef = useRef(null);

  // Prevent overlapping fetches
  const isFetchingRef = useRef(false);

  // Make absolute API "next" URL relative for axiosInstance
  const toRelative = (url) => url?.replace(/^https?:\/\/[^/]+\//, "") || null;

  const handleGetStartedClick = () => {
    navigate("/Products"); // Navigate to the /products page
  };

  const createSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, "-")
      .trim();
  };
  const ProjectData = (project) => {
    const projectSlug = createSlug(project.name);

    navigate(`/AppDetails/${projectSlug}`, {
      state: {
        id: project.id,
        project_type: project.project_type,
        project: project, // Pass the entire project object if needed
      },
    });
  };

  const fetchPage = async (
    url = "/display/api/v1/list-project-for-display",
    { append = false } = {}
  ) => {
    try {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setError(null);
      append ? setLoadingMore(true) : setLoading(true);

      const res = await api.get(toRelative(url));
      const data = res?.data?.data;

      const results = data?.results ?? [];
      setProjects((prev) => (append ? [...prev, ...results] : results));
      setNextUrl(toRelative(data?.next)); // null when no more pages
      setCount(data?.count ?? 0);
    } catch (err) {
      console.error("Error fetching projects list:", err);
      setError("Failed to load apps.");
    } finally {
      isFetchingRef.current = false;
      append ? setLoadingMore(false) : setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSingleProject = async () => {
      try {
        const res = await api.get(
          `/display/api/v1/show-single-project/${projectCode}`
        );
        if (res.data.status === "success") {
          setSingleProject(res.data.data);
        }
      } catch (error) {
        console.error("Error fetching single project:", error);
      }
    };

    fetchPage();
    fetchSingleProject();
  }, []);

  useEffect(() => {
    const container = listScrollRef.current;
    const sentinel = sentinelRef.current;
    if (!container || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && nextUrl && !loading && !loadingMore) {
          fetchPage(nextUrl, { append: true });
        }
      },
      {
        root: container, // observe inside the scrollable panel
        rootMargin: "200px", // start loading early
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextUrl, loading, loadingMore]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <div className="w-12 h-12 border-4 border-t-transparent border-[#7D5AE3] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <HomeHeader></HomeHeader>

      <div
        className="flex flex-col md:flex-row px-5 sm:px-10"
        style={{
          backgroundImage: `url(${Rectangle})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Left Column */}
        <div className="flex flex-col w-full md:w-[60%]">
          <div className="mt-5">
            <h1 className="font-bold text-3xl md:text-5xl">
              Empowering your vision
            </h1>
            <h1 className="font-bold text-3xl md:text-5xl">
              <span style={{ color: "#62676B" }}>with our</span>{" "}
              <span style={{ color: "#9367D7" }}>innovation</span>
            </h1>
          </div>

          {/* Divider Line */}
          <div
            className="w-[20%] h-1 mt-5"
            style={{ backgroundColor: "#9061DB" }}
          ></div>

          {/* Description Text */}
          <div className="mt-5 w-full md:w-[75%]">
            <p style={{ textAlign: "justify" }}>
              An exceptional suite of tools crafted to elevate your workflow.
              Designed for teams of every size and developed by a company that
              prioritizes your privacy at every step.
            </p>
          </div>

          {/* Get Started Button */}
          <div className="flex justify-center sm:justify-start">
            <div
              className="h-8 w-36 flex justify-around items-center mt-5 rounded cursor-pointer"
              style={{
                background: "linear-gradient(270deg, #9061DB 0%, #8777F3 100%)",
              }}
              onClick={handleGetStartedClick}
            >
              <span className="text-white font-bold text-sm">Get Started</span>
              <img src={ArrowRight} alt="Arrow" />
            </div>
          </div>

          {/* Image */}
          <img src={HomeImg} alt="" className=" mt-10 mr-5" />
        </div>

        {/* Right Column */}
        <div className="mt-5 w-full md:w-[40%] mb-5 shadow-md border border-2 p-4 border-[#CEC7FF33] rounded-xl relative bg-white">
          <span className="text-xl font-semibold">Featured Applications</span>

          {/* Featured Apps List */}
          <div
            ref={listScrollRef}
            className="overflow-y-auto flex flex-col gap-3 mt-5 custom-scrollbar"
            style={{ maxHeight: "500px", paddingRight: "8px" }}
          >
            {error && <div className="text-sm text-red-600">{error}</div>}

            {projects.map((project) => (
              <div
                key={project.id}
                className="flex gap-3 mt-2 items-center justify-between cursor-pointer"
                onClick={() => ProjectData(project)}
              >
                <img
                  src={project.logo || HomeImg}
                  alt={project.name}
                  className="w-14 h-14 shadow object-cover rounded-md"
                  onError={(e) => (e.currentTarget.src = HomeImg)}
                />
                <div className="flex flex-col gap-1 w-full">
                  <span className="font-bold text-[#60448c]">
                    {project.name}
                  </span>
                  <p className="text-xs line-clamp-2">{project.description}</p>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="black"
                  viewBox="0 0 24 24"
                  className="w-8 h-8"
                >
                  <path d="M10 6l6 6-6 6" />
                </svg>
              </div>
            ))}

            {/* Bottom mini loader */}
            {loadingMore && (
              <div className="py-3 text-center text-sm text-gray-600">
                Loading more…
              </div>
            )}

            {/* Invisible sentinel that triggers next page load */}
            <div ref={sentinelRef} style={{ height: 1 }} />

            {/* Optional: show end-of-list */}
            {/* {!nextUrl && projects.length >= count && (
              <div className="py-2 text-center text-xs text-gray-500">
                All items loaded.
              </div>
            )} */}
          </div>

          {/* Divider Line */}
          {/* <div
            className="w-full mt-5"
            style={{ backgroundColor: "black", height: "1px" }}
          ></div> */}

          {/* Explore All Products */}
          {/* <h1 className="text-base mt-2" style={{ color: "#4C3A68" }}>
            Explore all products
          </h1> */}
        </div>
      </div>

      {singleProject && (
        <div
          className="flex flex-col justify-between"
          style={{ backgroundColor: "#4C3A68" }}
        >
          {/* Header or Logo */}
          <div className="px-10 py-5 text-white flex flex-col md:flex-row gap-5 items-center md:items-start">
            {singleProject?.logo ? (
              <img
                src={singleProject.logo}
                alt="App Logo"
                className=" object-contain  h-20 sm:h-[240px]"
              />
            ) : (
              <h1 className="text-2xl font-bold">App Logo</h1>
            )}

            {/* Content block pinned near bottom */}
            <div className="flex flex-col w-full md:w-[50%]">
              <h1 className="text-4xl font-bold text-center md:text-left">
                {singleProject?.name || "App Name"}
              </h1>

              <span className="mt-2 text-lg text-center md:text-left">
                {singleProject?.tagline || "Your unified Workspace"}
              </span>

              <span
                className="mt-3  leading-relaxed text-center md:text-left"
                style={{ textAlign: "justify" }}
              >
                {singleProject?.description ||
                  `Power your entire business with a single, integrated cloud platform
            built to connect departments, streamline operations, and boost
            productivity across your organization.`}
              </span>

              <span
                className="flex gap-3 mt-5 h-8 justify-center font-semibold items-center w-full md:w-[30%] rounded text-sm cursor-pointer"
                style={{
                  background:
                    "linear-gradient(270deg, #9061DB 0%, #8777F3 100%)",
                }}
                onClick={() => (window.location.href = "https://onzitr.com/")}
              >
                <span
                  style={{ cursor: "pointer" }}
                  className="font-semibold text-sm text-white"
                >
                  Get Started Today
                </span>
                <img src={ArrowRight} alt="Arrow" />
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
